<template>
  <div class="header-create-section">
    <div class="header-create-content">
      <!-- Empty State: Makercrate tab -->
      <template v-if="!hasCartWithActivities && activeTab === 'makercrate'">
        <div class="mc-empty-state">
          <div class="mc-logo-wrapper">
            <img
              src="/images/maker-cart/makercrate-logo-v3.png"
              alt="Makercrate Logo"
              class="mc-logo-img"
            />
          </div>
          <div class="mc-empty-text">
            <h2 class="mc-empty-title">Makercrate</h2>
            <p class="mc-empty-subtitle">
              Makercrates are classroom-ready maker kits designed to help
              educators begin hands-on learning without sourcing materials,
              planning from scratch, or guessing where to start.
            </p>
          </div>
        </div>
      </template>

      <!-- Empty State: other tabs -->
      <template v-else-if="!hasCartWithActivities">
        <div class="create-illustration">
          <img
            src="/images/maker-cart/create-makercart-illustration.svg"
            alt="Create Makercart"
            class="create-image"
            style="height: 100px"
          />
        </div>
        <div class="create-text">
          <h2 class="create-title">No carts yet?</h2>
          <p class="create-subtitle">
            Start by creating your first Makercart and adding activities, or
            create from 'View your cart'
          </p>
        </div>
      </template>

      <!-- Carts with Activities State -->
      <div v-else class="carts-with-activities">
        <h2 class="carts-title">
          Fill your carts with activities to get started.
        </h2>
        <div class="carts-container">
          <div class="carts-container-inner">
            <!-- Show up to 3 carts total, prioritizing signature carts first -->
            <template
              v-for="cart in displayedCartsList"
              :key="cart.id || cart.type"
            >
              <!-- Signature Makercart -->
              <div
                v-if="cart.type === 'signature'"
                class="cart-item signature-cart"
              >
                <div class="cart-icon">
                  <img
                    src="/images/maker-cart/cart-bundle.svg"
                    alt="Signature Cart Badge"
                    class="cart-badge-icon"
                  />
                </div>
                <div class="cart-info">
                  <span class="cart-name">{{ cart.name }} - </span>
                  <span class="cart-activity-count"
                    >{{ cart.activityCount }}/{{
                      cart.maxActivities
                    }}
                    Activities</span
                  >
                </div>
              </div>

              <!-- Regular Makercart -->
              <div v-else-if="cart.type === 'regular'" class="cart-item">
                <div
                  class="cart-icon"
                  :style="
                    cart.activityCount < cart.maxActivities
                      ? { filter: 'grayscale(100%)' }
                      : {}
                  "
                >
                  <img
                    :src="`/images/maker-cart/cart-bundle.svg`"
                    alt="Cart Badge"
                    class="cart-badge-icon"
                  />
                </div>
                <div
                  class="cart-info"
                  :class="{
                    'all-bundled': cart.activityCount === cart.maxActivities,
                  }"
                >
                  <span class="cart-name">{{ cart.name }} - </span>
                  <span class="cart-activity-count"
                    >{{ cart.activityCount }}/{{
                      cart.maxActivities
                    }}
                    Activities</span
                  >
                </div>
              </div>

              <!-- Single Activities -->
              <div v-else-if="cart.type === 'single'" class="cart-item">
                <div class="cart-icon">
                  <img
                    src="/images/maker-cart/single-activity.svg"
                    alt="Cart Badge"
                    class="cart-badge-icon"
                  />
                </div>
                <div class="cart-info">
                  <span class="cart-name">Single Activities - </span>
                  <span class="cart-activity-count"
                    >{{ cart.count }} Activities</span
                  >
                </div>
              </div>
            </template>
          </div>
          <div v-if="remainingCartsCount > 0" class="more-carts">
            +{{ remainingCartsCount }} More
          </div>
        </div>
        <p class="carts-tip">
          Tip: Bundle up to 4 activities into a Makercart for the best value.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed, watch } from "vue";

const props = defineProps({
  activeTab: {
    type: String,
    default: "",
  },
  hasCartWithActivities: {
    type: Boolean,
    default: false,
  },
  displayedCarts: {
    type: Array,
    default: () => [],
  },
  singleActivities: {
    type: Array,
    default: () => [],
  },
  signatureMakercarts: {
    type: Array,
    default: () => [],
  },
  remainingCartsCount: {
    type: Number,
    default: 0,
  },
});

watch(
  () => props.singleActivities,
  () => {
    console.log("singleActivities", props.singleActivities);
  }
);

const displayedCartsList = computed(() => {
  const result = [];
  const maxCarts = 3;

  // Add signature carts first (up to maxCarts)
  const signatureCarts = props.signatureMakercarts
    .slice(0, maxCarts)
    .map((cart) => ({
      ...cart,
      type: "signature",
    }));
  result.push(...signatureCarts);

  // Add regular carts (up to remaining slots)
  const remainingSlots = maxCarts - result.length;
  if (remainingSlots > 0) {
    const regularCarts = props.displayedCarts
      .slice(0, remainingSlots)
      .map((cart) => ({
        ...cart,
        type: "regular",
      }));
    result.push(...regularCarts);
  }

  console.log("singleActivities", props.singleActivities);
  // Add single activities if there's still space and single activities exist
  // const finalRemainingSlots = maxCarts - result.length;
  if (props.singleActivities.length > 0) {
    result.push({
      type: "single",
      count: props.singleActivities.length,
    });
  }

  return result;
});
</script>

<style scoped>
/* Header Create Section Styles */
.header-create-section {
  margin-bottom: 24px;
}

.header-create-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  background: #f2f3fb;
  border-radius: 16px;
}

.create-illustration {
  flex-shrink: 0;
}

.create-image {
  object-fit: contain;
}

.create-text {
  color: var(--primary-blue);
}

.create-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: 32px;
  line-height: 40px;
  margin: 0 0 8px 0;
  color: var(--primary-blue);
}

.create-subtitle {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.08px;
  margin: 0;
  color: var(--primary-blue);
}

/* Makercrate empty state */
.mc-empty-state {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 16px;
  min-height: 0;
  min-width: 0;
}

.mc-logo-wrapper {
  flex-shrink: 0;
  width: 100px;
  height: 100px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mc-logo-img {
  width: 156.22%;
  height: auto;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: none;
}

.mc-empty-text {
  flex: 1;
  min-width: 0;
  color: var(--primary-blue);
}

.mc-empty-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: 32px;
  line-height: 40px;
  margin: 0 0 4px 0;
  color: var(--primary-blue);
  white-space: nowrap;
}

.mc-empty-subtitle {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.08px;
  margin: 0;
  color: var(--primary-blue);
}

/* Carts with Activities Styles */
.carts-with-activities {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.carts-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-xxl);
  line-height: 100%;
  margin: 0;
  color: var(--primary-blue);
}

.carts-container {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
}

.carts-container-inner {
  display: grid;
  grid-template-columns: repeat(3, minmax(200px, 1fr));
  flex: 1;
  gap: 16px;
  max-width: 100%;
}

.cart-item {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 8px 24px;
  background: #ffffff;
  border-radius: 24px;
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.3),
    0px 1px 3px 1px rgba(0, 0, 0, 0.15);
}

.signature-cart {
  background: #e6f4ef !important;
}

.cart-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cart-badge-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.cart-info {
  flex: 1;
  min-width: 0;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  line-height: 28px;
  color: #3b4648;
}

.cart-name {
  font-weight: 700;
}

.cart-activity-count {
  font-weight: 400;
}

.all-bundled {
  color: var(--primary-blue) !important;
}

.more-carts {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-large);
  line-height: 28px;
  color: var(--primary-blue);
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carts-tip {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-big);
  line-height: 24px;
  letter-spacing: 0.024px;
  margin: 0;
  color: var(--neutral-900);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .carts-container-inner {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .header-create-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .create-title {
    font-size: var(--fs-xl);
    line-height: 32px;
  }

  .create-subtitle {
    font-size: var(--fs-small);
    line-height: 20px;
  }

  /* Carts with Activities Responsive */
  .carts-title {
    font-size: var(--fs-xl);
    line-height: 32px;
  }

  .carts-container {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .cart-item {
    flex-direction: row;
    justify-content: flex-start;
    padding: 12px;
  }

  .cart-info {
    font-size: 18px;
    line-height: 24px;
  }

  .more-carts {
    font-size: 18px;
    line-height: 24px;
    align-self: center;
  }

  .carts-tip {
    font-size: var(--fs-small);
    line-height: 20px;
  }
}

@media (max-width: 480px) {
  .create-title {
    font-size: var(--fs-big);
    line-height: 28px;
  }

  .create-subtitle {
    font-size: 13px;
    line-height: 18px;
  }

  .header-create-section {
    display: none;
  }
}
</style>
